import type { Registry } from '../registry';

/**
 * Scene built-in.
 *
 * Auto-injects a `scene` site-function entry into the manifest. The function
 * is a raw pass-through: it forwards the DSL string to the host's
 * `registerSceneHandler(fn)` callback (if any) and also dispatches a
 * `mobeus:scene` `CustomEvent` on `window` for hosts that prefer DOM events.
 *
 * Mobeus does not parse the DSL — that's the host's responsibility.
 */
export function installSceneBuiltin(registry: Registry): void {
  registry.registerSystem('scene', {
    description:
      'Update the host page UI by sending a pipe-delimited DSL string. The host parses and renders it.',
    schema: {
      type: 'object',
      properties: { dsl: { type: 'string' } },
      required: ['dsl'],
    },
    fn: (args: { dsl: string }) => {
      const dsl = args?.dsl ?? '';

      // Always fire the DOM event for hosts that prefer it.
      try {
        window.dispatchEvent(new CustomEvent('mobeus:scene', { detail: { dsl } }));
      } catch (e) {
        console.warn('[Mobeus] failed to dispatch mobeus:scene event', e);
      }

      const handler = registry.getSceneHandler();
      if (handler) {
        try {
          handler(dsl);
        } catch (e) {
          return {
            success: false,
            error: `scene handler threw: ${String((e as Error)?.message ?? e)}`,
          };
        }
        return { success: true };
      }

      console.warn('[Mobeus] scene built-in enabled but no scene handler registered');
      return { success: false, error: 'no scene handler registered' };
    },
  });
}
